/**
 * Sprint T-18 · Notification Studio · Playground · Left panel.
 *
 * Template picker + Locale picker + Tenant picker + Context JSON editor.
 * All controlled from parent. Zero business logic.
 */
import React from "react";
import { RefreshCw, PlayCircle } from "lucide-react";
import JsonEditor from "./JsonEditor";
import ChannelIcon from "./ChannelIcon";

export default function PlaygroundLeftPanel({
  templates,
  templatesLoading,
  templateId,
  onTemplateId,
  locale,
  onLocale,
  tenantId,
  onTenantId,
  contextRaw,
  onContextChange,
  onFillDefaults,
  onValidate,
  missingContext = [],
  requiredContext = [],
}) {
  const selected = templates?.find((t) => t.template_id === templateId);
  // template.locales is an object · normalize to array of keys for the select
  const localesRaw = selected?.locales;
  const locales = Array.isArray(localesRaw)
    ? localesRaw
    : localesRaw && typeof localesRaw === "object"
    ? Object.keys(localesRaw)
    : ["es-ES"];

  return (
    <div
      className="flex flex-col gap-4 rounded border border-[var(--ag-border)] bg-[var(--ag-panel)] p-4"
      data-testid="studio-playground-left-panel"
    >
      {/* Row · template + locale + tenant */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-widest text-[var(--ag-text-dim)]">
            Template
          </span>
          <select
            value={templateId || ""}
            onChange={(e) => onTemplateId(e.target.value)}
            disabled={templatesLoading}
            data-testid="studio-playground-template-select"
            className="rounded border border-[var(--ag-border)] bg-black/30 px-2 py-1.5 text-xs text-[var(--ag-text)]"
          >
            <option value="">— seleccionar —</option>
            {(templates || []).map((t) => (
              <option key={t.template_id} value={t.template_id}>
                {t.template_id}
              </option>
            ))}
          </select>
          {selected && (
            <span className="flex items-center gap-1 text-[10px] text-[var(--ag-text-dim)]">
              <ChannelIcon channel={selected.channel} size={10} />
              {selected.channel} · topic: {selected.topic}
            </span>
          )}
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-widest text-[var(--ag-text-dim)]">
            Locale
          </span>
          <select
            value={locale || "es-ES"}
            onChange={(e) => onLocale(e.target.value)}
            data-testid="studio-playground-locale-select"
            className="rounded border border-[var(--ag-border)] bg-black/30 px-2 py-1.5 text-xs text-[var(--ag-text)]"
          >
            {locales.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-widest text-[var(--ag-text-dim)]">
            Tenant (opcional)
          </span>
          <input
            type="text"
            value={tenantId || ""}
            onChange={(e) => onTenantId(e.target.value || null)}
            placeholder="uuid tenant (aplica override)"
            data-testid="studio-playground-tenant-input"
            className="rounded border border-[var(--ag-border)] bg-black/30 px-2 py-1.5 font-mono text-[11px] text-[var(--ag-text)]"
          />
        </label>
      </div>

      {/* Required context hint */}
      {selected && requiredContext.length > 0 && (
        <div
          className="rounded border border-[var(--ag-border)]/70 bg-black/20 px-3 py-2 text-[11px]"
          data-testid="studio-playground-required-context"
        >
          <span className="text-[var(--ag-text-dim)]">Requeridos:</span>{" "}
          {requiredContext.map((k) => (
            <span
              key={k}
              className={`mx-0.5 rounded px-1 font-mono ${
                missingContext.includes(k)
                  ? "bg-rose-500/20 text-rose-300"
                  : "bg-emerald-500/20 text-emerald-300"
              }`}
              data-testid={`studio-playground-req-${k}`}
            >
              {k}
            </span>
          ))}
        </div>
      )}

      {/* JSON editor */}
      <JsonEditor
        value={contextRaw}
        onChange={onContextChange}
        label="Context JSON"
        testid="studio-playground-context-editor"
        rows={14}
      />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onFillDefaults}
          disabled={!selected}
          data-testid="studio-playground-fill-defaults-btn"
          className="flex items-center gap-1 rounded border border-[var(--ag-border)] px-2.5 py-1 text-[11px] text-[var(--ag-text-dim)] hover:border-[var(--ag-gold)]/50 hover:text-[var(--ag-text)] disabled:opacity-50"
        >
          <RefreshCw size={11} />
          Rellenar defaults
        </button>
        <button
          type="button"
          onClick={onValidate}
          disabled={!selected}
          data-testid="studio-playground-validate-btn"
          className="flex items-center gap-1 rounded border border-[var(--ag-border)] px-2.5 py-1 text-[11px] text-[var(--ag-text-dim)] hover:border-[var(--ag-gold)]/50 hover:text-[var(--ag-text)] disabled:opacity-50"
        >
          <PlayCircle size={11} />
          Renderizar preview
        </button>
      </div>
    </div>
  );
}
